import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Banner, Card, DataTable, Empty, Field, Modal, Pager, Stat, Tabs,
  inr, inrCompact, useDebounced, usePagedList, useSyncFilter, usePolling, when,
} from "../../components/sc/ui";
import { WebhookNote } from "./PayinGateway";
import { DateRange, DeliveryList, TxList, TxToolbar, TxnDrawer, errMsg, exportCsv } from "./gateway/common";

const STATUSES = ["all", "pending", "success", "failed", "review", "error"];
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const LEDGER_KINDS = {
  payin_credit: "Payin credit", payin_adjustment: "Payin adjustment", payout_debit: "Payout",
  payout_refund: "Payout refund", manual_adjustment: "Adjustment by SuperCash",
};

export default function Payouts() {
  const [tab, setTab] = useState("payouts");
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bump, setBump] = useState(0);

  const loadSummary = useCallback(() => api.get("/gateway/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  useEffect(() => { api.get("/gateway/profile").then((r) => setProfile(r.data)).catch(() => {}); loadSummary(); }, [loadSummary]);
  usePolling(loadSummary, 8000);

  const s = (o, keys) => keys.reduce((a, k) => ({ c: a.c + (o?.[k]?.count || 0), v: a.v + (o?.[k]?.amount || 0) }), { c: 0, v: 0 });
  const paidToday = s(summary?.payouts_today, ["success"]);
  const processing = s(summary?.payouts, ["pending", "creating", "review"]);
  const failed = s(summary?.payouts_today, ["failed", "error"]);
  const enabled = !!profile?.payout_enabled && !!profile?.gateway_live;

  return (
    <div className="ui-page" data-testid="payouts-page">
      {profile && !profile.gateway_live && <Banner>The payment gateway is being set up by SuperCash. Payouts open once it's live.</Banner>}
      {profile && profile.gateway_live && !profile.payout_enabled && <Banner>Payouts aren't enabled for your account yet. Contact SuperCash support.</Banner>}

      <div className="ui-grid ui-grid-4">
        <Stat accent label="Available balance" value={inrCompact(summary?.balance)} foot="Payins credited − payouts" testId="po-balance" />
        <Stat label="Paid out today" value={inrCompact(paidToday.v)} foot={`${paidToday.c} payouts`} />
        <Stat label="Processing" value={processing.c.toLocaleString("en-IN")} foot={inr(processing.v)} />
        <Stat label="Failed today" value={failed.c.toLocaleString("en-IN")} foot="auto-refunded to balance" />
      </div>

      <div className="ui-between">
        <Tabs value={tab} onChange={setTab} testIdPrefix="po-tab" tabs={[
          { key: "payouts", label: "Payouts" },
          { key: "statement", label: "Statement" },
          { key: "callbacks", label: "Callbacks" },
        ]} />
        <button className="ui-btn ui-btn-primary" data-testid="po-new" disabled={!enabled} onClick={() => setCreating(true)}>+ New payout</button>
      </div>

      {tab === "payouts" && <PayoutList key={bump} counts={summary?.payouts} onChanged={loadSummary} />}
      {tab === "statement" && <Statement />}
      {tab === "callbacks" && <WebhookNote payout url={profile?.webhook_url} />}
      {tab === "callbacks" && <DeliveryList kind="payout" />}
      {creating && profile && (
        <NewPayout balance={summary?.balance} onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); setTab("payouts"); setBump((b) => b + 1); loadSummary(); }} />
      )}
    </div>
  );
}

function PayoutList({ counts, onChanged }) {
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput);
  const [openId, setOpenId] = useState(null);
  const list = usePagedList((p) => api.get("/gateway/payouts", { params: p }).then((r) => r.data), { status: "all", q: "", date_from: "", date_to: "" }, 25);
  useSyncFilter(list.setFilter, "q", q);
  usePolling(list.load, 4000, list.page === 1);

  const rows = list.data.items.map((p) => ({
    id: p.id, status: p.status, at: p.created_at,
    title: p.order_id, sub: p.source === "api" ? "API" : "Dashboard",
    who: p.beneficiary?.name, whoSub: `••${p.beneficiary?.account_last4} · ${p.beneficiary?.ifsc}`,
    ref: p.utr || (p.failure_reason ? p.failure_reason.replace(/_/g, " ") : null), refSub: p.refunded ? "refunded to balance" : null,
    amount: p.amount,
  }));

  return (
    <Card pad={false} data-testid="po-table">
      <div className="ui-row" style={{ gap: 8, padding: "14px 16px 0" }}>
        <h3 className="ui-card-title">Payouts</h3>
        <span className="ui-mono ui-faint" style={{ fontSize: 12 }}>{list.data.total.toLocaleString("en-IN")}</span>
      </div>
      <TxToolbar list={list} qInput={qInput} setQInput={setQInput} statuses={STATUSES} counts={counts}
        placeholder="Search order ID, UTR, account, name…" onExport={() => exportCsv("/gateway/payouts/export", list.filters)} />
      <div style={{ height: 10 }} />
      <TxList rows={rows} loading={list.loading} fresh={list.fresh} activeId={openId} onOpen={setOpenId}
        headers={["Order", "Beneficiary", "UTR", "Time", "Amount", "Status"]}
        empty={<Empty icon="📤" title="No payouts found">Send one with “New payout” or via the API (Developers → Gateway API).</Empty>}
        footer={list.data.total > 0 && <Pager page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />} />
      {openId && <TxnDrawer kind="payout" id={openId} onClose={() => setOpenId(null)} onChanged={() => { list.load(); onChanged(); }} />}
    </Card>
  );
}

function Statement() {
  const list = usePagedList((p) => api.get("/gateway/ledger", { params: p }).then((r) => r.data), { kind: "all", date_from: "", date_to: "" }, 25);
  usePolling(list.load, 8000, list.page === 1);
  const cols = [
    { key: "at", label: "Date", render: (r) => <span className="ui-mono" style={{ fontSize: 12 }}>{when(r.created_at, true)}</span> },
    { key: "kind", label: "Type", render: (r) => (
      <span style={{ display: "block", minWidth: 0 }}>
        <span className="ui-cell-main">{LEDGER_KINDS[r.kind] || r.kind}</span>
        <span className="ui-cell-sub">{r.order_id || r.note || ""}</span>
      </span>) },
    { key: "amount", label: "Amount", num: true, render: (r) => (
      <span className="ui-cell-main" style={{ color: r.amount >= 0 ? "#0f7a45" : "#b3261e" }}>{r.amount >= 0 ? "+" : "−"}{inr(Math.abs(r.amount))}</span>) },
    { key: "bal", label: "Balance", num: true, render: (r) => <span className="ui-mono" style={{ fontSize: 12.5 }}>{inr(r.balance_after)}</span> },
  ];
  return (
    <Card pad={false} title="Balance statement" sub="Every movement of your SuperCash balance — credits from payins, debits for payouts, refunds."
      actions={<>
        <select className="ui-select" value={list.filters.kind} onChange={(e) => list.setFilter("kind", e.target.value)}>
          <option value="all">All types</option>
          {Object.entries(LEDGER_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <DateRange filters={list.filters} setFilter={list.setFilter} />
      </>}>
      <DataTable testId="po-ledger" columns={cols} rows={list.data.items} loading={list.loading} fresh={list.fresh}
        empty={<Empty icon="📒" title="No balance movements yet">Successful payins credit your balance here.</Empty>} />
      {list.data.total > 0 && <Pager page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />}
    </Card>
  );
}

function NewPayout({ balance, onClose, onCreated }) {
  // Order ID is generated by SuperCash; bank name isn't needed (IFSC identifies the bank).
  const blank = { amount: "", account_holder_name: "", account_number: "", account2: "", ifsc: "" };
  const [f, setF] = useState(blank);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: k === "ifsc" ? e.target.value.toUpperCase().trim() : e.target.value }));
  const amount = parseFloat(f.amount) || 0;

  const validate = () => {
    if (!(amount > 0)) return "Enter an amount in ₹";
    if (f.account_holder_name.trim().length < 2) return "Enter the account holder's name";
    if (!/^[A-Za-z0-9]{6,36}$/.test(f.account_number.trim())) return "Account number should be 6–36 letters/digits";
    if (f.account_number.trim() !== f.account2.trim()) return "Account numbers don't match";
    if (!IFSC_RE.test(f.ifsc)) return "IFSC looks invalid (e.g. HDFC0001234)";
    if (balance != null && amount > balance) return `Insufficient balance — you have ${inr(balance)}`;
    return null;
  };
  const review = () => {
    const err = validate();
    if (err) return toast.error(err);
    setStep("confirm");
  };
  const send = async () => {
    setBusy(true);
    try {
      const { account2, ...body } = f;
      const payload = Object.fromEntries(Object.entries({ ...body, amount }).filter(([, v]) => v !== ""));
      const { data } = await api.post("/gateway/payouts", payload);
      toast.success(data.status === "review" ? "Payout submitted — awaiting bank confirmation" : "Payout sent");
      onCreated(data);
    } catch (e) {
      toast.error(errMsg(e));
      setStep("form");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={step === "form" ? "New payout" : "Confirm payout"} onClose={onClose} footer={step === "form" ? <>
      <button className="ui-btn" onClick={onClose}>Cancel</button>
      <button className="ui-btn ui-btn-primary" data-testid="po-review" onClick={review}>Review →</button>
    </> : <>
      <button className="ui-btn" onClick={() => setStep("form")} disabled={busy}>← Edit</button>
      <button className="ui-btn ui-btn-primary" data-testid="po-submit" onClick={send} disabled={busy}>{busy ? "Sending…" : `Send ${inr(amount)}`}</button>
    </>}>
      {step === "form" ? (
        <>
          <Field label="Amount ₹" hint={`Available ${inr(balance)}`}><input className="ui-input" data-testid="po-amount" type="number" min="1" step="0.01" value={f.amount} onChange={set("amount")} autoFocus /></Field>
          <Field label="Account holder name"><input className="ui-input" data-testid="po-holder" value={f.account_holder_name} onChange={set("account_holder_name")} placeholder="As per bank records" /></Field>
          <div className="ui-grid ui-grid-2">
            <Field label="Account number"><input className="ui-input is-mono" data-testid="po-account" value={f.account_number} onChange={set("account_number")} autoComplete="off" /></Field>
            <Field label="Confirm account number"><input className="ui-input is-mono" data-testid="po-account2" value={f.account2} onChange={set("account2")} onPaste={(e) => e.preventDefault()} autoComplete="off" /></Field>
          </div>
          <Field label="IFSC"><input className="ui-input is-mono" data-testid="po-ifsc" value={f.ifsc} onChange={set("ifsc")} maxLength={11} placeholder="HDFC0001234" /></Field>
        </>
      ) : (
        <>
          <div className="ui-stat-value" style={{ fontSize: 30 }}>{inr(amount)}</div>
          <div style={{ fontSize: 15 }}>to <b>{f.account_holder_name}</b></div>
          <div className="ui-kv">
            <div className="ui-kv-item"><span className="ui-eyebrow">Account</span><span className="ui-kv-val">{f.account_number}</span></div>
            <div className="ui-kv-item"><span className="ui-eyebrow">IFSC</span><span className="ui-kv-val">{f.ifsc}</span></div>
          </div>
          <Banner>Bank transfers can't be reversed once processed. Check the account details carefully.</Banner>
        </>
      )}
    </Modal>
  );
}
